import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/firebaseconfig';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Save, ArrowLeft, Trash2, Plus, Zap, Building2, AlignLeft, Info } from 'lucide-react';
import AdminSidebar from './adminsidebar';

const FIXED_STEPS = [
  "Site Feasibility", "Document Verification", "Document Compliance", "Application of DISCOM",
  "Site Work Of Civil & Electrical", "Equipment Installation (A. HTMC Panel, B. VCB, C. Transformer, D. LT Panel)",
  "CEIG Approval of Site", "Equipment Approval from DISCOM", "NOC from CEIG for Project",
  "Meter Released & Charging up the Site", "Punch Point Rectification", "HOTO Done", "Site Completed"
];

const AdminEditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    const fetchProject = async () => {
      const docRef = doc(db, "projects", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setProject(data);
        setSteps(data.steps || []);
      } else {
        alert("Project not found!");
        navigate('/admin/dashboard');
      }
      setLoading(false);
    };
    fetchProject();
  }, [id, navigate]);

  const handleProjectChange = (e) => {
    setProject({ ...project, [e.target.name]: e.target.value });
  };

  const handleStepChange = (index, e) => {
    const { name, value } = e.target;
    const newSteps = [...steps];
    newSteps[index][name] = value;
    setSteps(newSteps);
  };

  const addStep = () => {
    setSteps([...steps, { title: FIXED_STEPS[0], desc: '', status: 'pending' }]);
  };

  const removeStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const projectRef = doc(db, "projects", id);
      await updateDoc(projectRef, {
        ...project,
        steps: steps,
        percentage: Number(project.percentage),
        kilowatt: Number(project.kilowatt),
        lastUpdated: serverTimestamp()
      });
      alert("Project Updated Successfully!");
    } catch (error) {
      console.error("Update Error:", error);
      alert("Failed to update project.");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Project Details...</div>;

  return (
    
        <div className="max-w-900 mx-auto">
          
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button onClick={() => navigate(-1)} className="btn btn-link text-dark text-decoration-none p-0 d-flex align-items-center">
              <ArrowLeft size={20} className="me-2" /> Back to List
            </button>
            <div className="text-end">
              <h3 className="fw-bold mb-0 text-dark">{project.title}</h3>
              <span className="text-success fw-semibold small uppercase tracking-wider">{project.client}</span>
            </div>
          </div>

          <form onSubmit={handleUpdate}>
            {/* Core Project Details */}
            <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
              <h6 className="fw-bold text-muted mb-3 d-flex align-items-center gap-2">
                <Info size={18} /> Primary Information
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="small text-muted mb-1">Project Title</label>
                  <input type="text" name="title" className="form-control bg-light border-0" value={project.title} onChange={handleProjectChange} />
                </div>
                <div className="col-md-6">
                  <label className="small text-muted mb-1">Category (e.g., Solar, EV Station)</label>
                  <input type="text" name="category" className="form-control bg-light border-0" value={project.category} onChange={handleProjectChange} />
                </div>
                <div className="col-md-4">
                  <label className="small text-muted mb-1">Capacity (kW)</label>
                  <input type="number" name="kilowatt" className="form-control bg-light border-0" value={project.kilowatt} onChange={handleProjectChange} />
                </div>
                <div className="col-md-4">
                  <label className="small text-muted mb-1">Work Progress (%)</label>
                  <input type="number" name="percentage" className="form-control border-success fw-bold" value={project.percentage} onChange={handleProjectChange} />
                </div>
                <div className="col-md-4">
                  <label className="small text-muted mb-1">Project City</label>
                  <input type="text" name="city" className="form-control bg-light border-0" value={project.city} onChange={handleProjectChange} />
                </div>
                <div className="col-md-12">
                   <label className="small text-muted mb-1">Image URL (ImgBB Direct Link)</label>
                   <input type="text" name="imageUrl" className="form-control bg-light border-0" value={project.imageUrl} onChange={handleProjectChange} />
                </div>
              </div>
            </div>

            {/* Descriptions Area */}
            <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
              <h6 className="fw-bold text-muted mb-3 d-flex align-items-center gap-2">
                <AlignLeft size={18} /> Project Descriptions
              </h6>
              <div className="mb-3">
                <label className="small text-muted mb-1">Short Description (Displays on Cards)</label>
                <input type="text" name="desc" className="form-control bg-light border-0" value={project.desc} onChange={handleProjectChange} />
              </div>
              <div>
                <label className="small text-muted mb-1">Full Detailed Description</label>
                <textarea name="fullDesc" rows="5" className="form-control bg-light border-0" value={project.fullDesc} onChange={handleProjectChange}></textarea>
              </div>
            </div>

            {/* Execution Roadmap */}
            <h5 className="fw-bold mb-3">Live Execution Roadmap</h5>
            {steps.map((step, index) => (
              <div key={index} className={`card border-0 shadow-sm rounded-4 p-3 mb-3 ${step.status === 'completed' ? 'border-start border-5 border-success' : ''}`}>
                <div className="row align-items-center g-3">
                  <div className="col-md-6">
                    <select name="title" className="form-select border-0 bg-light fw-bold" value={step.title} onChange={(e) => handleStepChange(index, e)}>
                      {FIXED_STEPS.map((s, i) => <option key={i} value={s}>{i + 1}. {s}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <select name="status" className={`form-select border-0 ${step.status === 'completed' ? 'text-success fw-bold' : 'text-warning'}`} value={step.status} onChange={(e) => handleStepChange(index, e)}>
                      <option value="pending">Pending</option>
                      <option value="in-progress">In-Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div className="col-md-2 text-end">
                    <button type="button" className="btn btn-link text-danger" onClick={() => removeStep(index)}><Trash2 size={18}/></button>
                  </div>
                  <div className="col-12 mt-0">
                    <input type="text" name="desc" placeholder="Step-specific notes..." className="form-control form-control-sm border-0 bg-transparent text-muted" value={step.desc} onChange={(e) => handleStepChange(index, e)} />
                  </div>
                </div>
              </div>
            ))}

            <div className="d-flex gap-3 mt-4 mb-5">
              <button type="button" onClick={addStep} className="btn btn-outline-dark rounded-pill px-4 fw-bold">
                <Plus size={18} className="me-2" /> Add Step
              </button>
              <button type="submit" className="btn btn-success rounded-pill px-5 py-3 fw-bold shadow-sm flex-grow-1 text-uppercase tracking-wider">
                <Save size={20} className="me-2" /> Update Website Data
              </button>
            </div>
          </form>
        </div>
  );
};

export default AdminEditProject;